/**
 * 事件总线
 * 实现发布-订阅模式，用于模块间通信
 */
export class EventBus {
    constructor() {
        this.listeners = new Map();
    }

    /**
     * 订阅事件
     * @param {string} event 事件名称
     * @param {Function} callback 回调函数
     * @param {Object} context 回调函数的上下文
     * @returns {Function} 取消订阅的函数
     */
    on(event, callback, context = null) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }

        const listener = { callback, context };
        this.listeners.get(event).push(listener);

        // 返回取消订阅的函数
        return () => this.off(event, callback);
    }

    /**
     * 订阅一次性事件
     * @param {string} event 事件名称
     * @param {Function} callback 回调函数
     * @param {Object} context 回调函数的上下文
     */
    once(event, callback, context = null) {
        const wrapper = (...args) => {
            this.off(event, wrapper);
            callback.apply(context, args);
        };
        this.on(event, wrapper, context);
    }

    /**
     * 取消订阅
     * @param {string} event 事件名称
     * @param {Function} callback 回调函数
     */
    off(event, callback) {
        if (!this.listeners.has(event)) return;

        const listeners = this.listeners.get(event);
        const index = listeners.findIndex(l => l.callback === callback);

        if (index !== -1) {
            listeners.splice(index, 1);
        }

        if (listeners.length === 0) {
            this.listeners.delete(event);
        }
    }

    /**
     * 发布事件
     * @param {string} event 事件名称
     * @param  {...any} args 传递给回调的参数
     */
    emit(event, ...args) {
        if (!this.listeners.has(event)) return;

        const listeners = this.listeners.get(event).slice();
        for (const listener of listeners) {
            listener.callback.apply(listener.context, args);
        }
    }

    /**
     * 清除所有事件监听
     * @param {string} event 可选，指定清除的事件
     */
    clear(event = null) {
        if (event) {
            this.listeners.delete(event);
        } else {
            this.listeners.clear();
        }
    }
}

// 游戏事件常量
export const GameEvents = {
    // 游戏状态
    GAME_START: 'game:start',
    GAME_PAUSE: 'game:pause',
    GAME_RESUME: 'game:resume',
    GAME_COMPLETE: 'game:complete',
    GAME_RESET: 'game:reset',

    // 拼图事件
    PIECE_PICKUP: 'piece:pickup',
    PIECE_DROP: 'piece:drop',
    PIECE_SNAP: 'piece:snap',
    PIECE_ROTATE: 'piece:rotate',
    PUZZLE_GENERATED: 'puzzle:generated',
    PUZZLE_SCATTERED: 'puzzle:scattered',

    // 图片事件
    IMAGE_LOADED: 'image:loaded',
    IMAGE_ERROR: 'image:error',

    // UI事件
    CONFIG_CHANGED: 'config:changed',
    TIMER_TICK: 'timer:tick',
    SCORE_CHANGED: 'score:changed'
};

// 创建全局事件总线实例
export const globalEventBus = new EventBus();

export default EventBus;
